/**
 * RequireTier — gate child routes behind a minimum subscription tier.
 *
 * Wraps a route element in <App.tsx>. If the user's tier is below
 * `minimum`, renders `<UpgradeNudge>` instead of the children. Admins
 * always pass regardless of their nominal tier (admin override).
 *
 * Behaviour vs <AuthGate>:
 *   • <AuthGate>     — "must be logged in" (any tier including free).
 *                       Bounces to /login if not authed.
 *   • <RequireTier>  — "must be at least `minimum` tier." Assumes
 *                       authed (compose inside <AuthGate>). Renders
 *                       the upgrade nudge in-place rather than
 *                       redirecting so the URL stays stable for
 *                       sharing.
 *
 * Usage:
 *   <Route element={<AuthGate><RequireTier minimum="pro">
 *     <AppShell><Risk /></AppShell>
 *   </RequireTier></AuthGate>} />
 */

import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import { tierAtLeast } from '../../lib/types';
import type { Tier } from '../../lib/types';
import UpgradeNudge from './UpgradeNudge';

interface RequireTierProps {
  minimum: Tier;
  /** Optional label for the upgrade nudge: "feature X requires Pro" */
  featureLabel?: string;
  children: React.ReactNode;
}

const RequireTier: React.FC<RequireTierProps> = ({ minimum, featureLabel, children }) => {
  const { loading, tier, isAdmin } = useAuth();

  // Don't render the nudge during the initial /api/me probe — the
  // user might actually have the tier, we just don't know yet.
  // Same approach <AuthGate> uses for the auth-loading state.
  if (loading) {
    return (
      <div className="gv-auth-redirect" role="status" aria-live="polite">
        <span className="gv-spinner" aria-hidden="true" />
        <span style={{ marginLeft: '0.6rem', color: 'var(--text-3)', fontSize: '0.85rem' }}>
          Verifying access…
        </span>
      </div>
    );
  }

  if (isAdmin || tierAtLeast(tier, minimum)) {
    return <>{children}</>;
  }

  return <UpgradeNudge required={minimum} currentTier={tier} featureLabel={featureLabel} />;
};

export default RequireTier;
